import React, { useEffect, useState } from "react"
import axios from "axios"
import { toast } from 'sonner'
import { Tooltip } from "@material-tailwind/react"
import { ArrowDownTrayIcon } from "@heroicons/react/24/outline"
import { ArrowPathRoundedSquareIcon, InformationCircleIcon, TrashIcon } from "@heroicons/react/24/solid"
import DataTableShell from "../../../../Components/table/DataTableShell"
import TableToolbar from "../../../../Components/table/TableToolbar"
import {
    TableActionGroup,
    TableEmptyRow,
    TableIdCell,
    TableSkeletonRows,
    TableStatusChip,
} from "../../../../Components/table/TableCells"
import useClientTable from "../../../../Components/table/useClientTable"

const TABLE_HEAD = ["Request ID", "Deal Name", "Requested by", "Status", "Actions"]
const newestFirst = (a, b) => new Date(b.createdAt) - new Date(a.createdAt)

export function AmzDealsTable({isAltered}) {

    const [dealBatches, setDealBatches] = useState([])
    const [isChanged, setIsChanged] = useState(false)
    const [isLoading, setIsLoading] = useState(true)
    const {
        currentPage,
        setCurrentPage,
        pageItems,
        totalPages,
        totalFiltered,
    } = useClientTable({
        data: dealBatches,
        pageSize: 10,
        sortFn: newestFirst,
    })

    useEffect(() => {
        axios.get('/api/amz-deals/all-batches')
            .then(response => {
                if (response.data.message) {
                    console.log("Cannot get batches")
                } else {
                    setDealBatches(response.data.batches)
                    console.log(response.data.batches)
                } 
            })
            .catch(error => {
                console.error('Error:', error)
            })
            .finally(() => setIsLoading(false))
    }, [isAltered, isChanged])

    const handleDeleteDeals = (batchId) => {
        const isConfirmed = window.confirm(`Are you sure you want to delete this request: ${batchId}?`)

        if (isConfirmed) {
            axios.delete(`/api/amz-deals/delete/${batchId}`)
                .then(response => {
                    if (response.data.message) {
                        toast.success(`${response.data.message}`)
                        setIsChanged(prevState => !prevState)
                    } else {
                        toast.error(`${response.data.message}`)
                        console.log('Error:', response.data.error)
                    }
                })
                .catch(error => {
                    console.error('Error:', error)
                })
        }
    }

    return (
        <DataTableShell
            minWidth="820px"
            toolbar={
                <TableToolbar
                    kicker="Amazon deals"
                    title="Deal Requests"
                    meta={`${totalFiltered} requests`}
                />
            }
            currentPage={currentPage}
            setCurrentPage={setCurrentPage}
            totalPages={totalPages}
        >
            <thead>
                <tr>
                    {TABLE_HEAD.map(head => <th key={head}>{head}</th>)}
                </tr>
            </thead>
            <tbody>
                {isLoading ? (
                    <TableSkeletonRows columns={TABLE_HEAD.length} rows={8} />
                ) : pageItems.length === 0 ? (
                    <TableEmptyRow
                        columns={TABLE_HEAD.length}
                        title="No Amazon deal requests found"
                        description="Submitted deal requests will appear here."
                    />
                ) : pageItems.map(({ batchId, dealsId, addedBy, status, createdAt }) => {
                const date = new Date(createdAt)
                const formattedTime = date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: true })
                        
                const options = { month: 'long', day: 'numeric', year: 'numeric' }
                const formattedDate = date.toLocaleDateString('en-US', options)
                const formattedDateTime = `${formattedDate} ${formattedTime}`
                const statusName = status?.name || 'error'

                return (
                    <tr key={batchId}>
                    <td><TableIdCell>{batchId}</TableIdCell></td>
                    <td>{dealsId}</td>
                    <td>
                        <div className="flex items-center gap-2">
                        <Tooltip content={`${formattedDateTime}`}>
                            <InformationCircleIcon className="h-5 w-5 text-muted"/>
                        </Tooltip>                 
                        {addedBy}
                        </div>
                    </td>
                    <td>
                        <Tooltip content={status?.message || statusName}>
                            <span><TableStatusChip status={statusName} /></span>
                        </Tooltip>
                    </td>
                    <td>
                        <TableActionGroup>
                            <Tooltip content="Download is not available yet">
                                <button
                                    type="button"
                                    className="nx-btn-ghost !px-2 opacity-40"
                                    disabled
                                    aria-label={`Download deal request ${batchId} unavailable`}
                                >
                                    <ArrowDownTrayIcon className="h-5 w-5"/>
                                </button>
                            </Tooltip>
                            <Tooltip content="Resync is not available yet">
                                <button
                                    type="button"
                                    className="nx-btn-ghost !px-2 opacity-40"
                                    disabled
                                    aria-label={`Resync deal request ${batchId} unavailable`}
                                >
                                    <ArrowPathRoundedSquareIcon className="h-5 w-5"/>
                                </button>
                            </Tooltip>
                            <Tooltip content="Delete request">
                                <button
                                    type="button"
                                    className="nx-btn-ghost !px-2 text-red-400"
                                    onClick={() => handleDeleteDeals(batchId)}
                                    aria-label={`Delete deal request ${batchId}`}
                                >
                                    <TrashIcon className="h-5 w-5"/>
                                </button>
                            </Tooltip>
                        </TableActionGroup>
                    </td>
                </tr>
                )})}
            </tbody>
        </DataTableShell>
    )
}